import {Alert, Box, InputAdornment, Typography} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {DraggableTag, ITag} from './DraggableTag';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import {t} from 'i18next';
import {regexValidationField} from './CustomFieldDrawer';
import AurButton from '../../../common/aur-button/AurButton';
import {isEmpty, isEqual, noop, some} from 'lodash';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import {ErrorDialog} from './ErrorDialog';
import {
  RenameConfirmDialog,
  RenameConfirmDialogHandle,
} from './RenameConfirmDialog';

export interface IDraggableTagListProps {
  tags: ITag[];
  showTagError?: boolean;
  dismissTagError?: () => void;
  usedTagError?: boolean;
  dismissUsedError?: () => void;
  addTag: (value: string) => void;
  deleteTag: (tagId: number) => void;
  editTag: (tag: ITag) => Promise<boolean>;
  handleReorder?: (tags: ITag[]) => void;
  updateTags?: (tags: ITag[], initialTagsRefresh: boolean) => void;
  onSaveChanges: (tags: ITag[]) => void;
  onToggleReorderChanges?: (isEditingOrder: boolean) => void;
  onOrderChanges?: (isOrderChanged: boolean) => void;
  canEditTags?: boolean;
}

const DraggableTagList: React.FC<IDraggableTagListProps> = ({
  tags: resTags,
  showTagError,
  dismissTagError,
  usedTagError,
  dismissUsedError,
  addTag,
  deleteTag,
  editTag,
  handleReorder,
  updateTags,
  onSaveChanges,
  onToggleReorderChanges,
  onOrderChanges,
  canEditTags = true,
}) => {
  const [newTag, setNewTag] = useState<string>('');
  const [duplicateError, setDuplicateError] = useState<boolean>(false);
  const [isEditingTag, setIsEditingTag] = useState<ITag | undefined>();
  const [isEditingOrder, setIsEditingOrder] = useState<boolean>(false);
  const initialTags = useRef<ITag[]>(resTags);
  const updatedTags = useRef<ITag[]>(resTags);
  const [tags, setTags] = useState<ITag[]>(resTags);
  const [tagError, setTagError] = useState<boolean>(false);
  const renameDialogRef = useRef<RenameConfirmDialogHandle | null>(null);

  useEffect(() => {
    if (isEmpty(initialTags.current)) {
      initialTags.current = [...resTags];
    }
    updatedTags.current = [...resTags];
    setTags([...resTags]);
    return () => {
      initialTags.current = [];
    };
  }, [resTags]);

  const isChangedInTags = useMemo(
    () => !isEqual(initialTags.current, tags),
    [initialTags, tags]
  );

  useEffect(() => {
    onOrderChanges?.(isChangedInTags);
  }, [isChangedInTags, onOrderChanges]);

  useEffect(() => {
    onToggleReorderChanges?.(isEditingOrder);
  }, [isEditingOrder, onToggleReorderChanges]);

  useEffect(() => {
    setTagError(false);
    setDuplicateError(false);
  }, [isEditingTag]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTagError(false);
      const value = e.target.value;
      setNewTag(value);
      const isDuplicateTag = some(tags, (tag) =>
        isEditingTag && isEditingTag?.tagValueId === tag.tagValueId
          ? false
          : tag.tagValue.toLowerCase() === value.toLowerCase()
      );
      if (isDuplicateTag) {
        setDuplicateError(true);
      } else {
        setDuplicateError(false);
      }
    },
    [isEditingTag, tags]
  );

  const moveTag = useCallback((fromIndex: number, toIndex: number) => {
    const [movedTag] = updatedTags.current.splice(fromIndex, 1);
    updatedTags.current.splice(toIndex, 0, movedTag);
    setTags([...updatedTags.current]);
  }, []);

  const validateTagName = useCallback(() => {
    setTagError(newTag ? !regexValidationField.test(newTag) : false);
  }, [newTag]);

  const handleAddTag = useCallback(() => {
    validateTagName();
    if (newTag.trim() && newTag.length <= 50 && !tagError) {
      addTag(newTag);
      setNewTag('');
    }
  }, [validateTagName, newTag, tagError, addTag]);

  const onEditTagClicked = useCallback((tag: ITag) => {
    if (!canEditTags) return;
    setIsEditingTag(tag);
    setNewTag(tag.tagValue);
  }, [canEditTags]);

  const onEditTagCancel = useCallback(() => {
    setIsEditingTag(undefined);
    setNewTag('');
  }, []);

  const updateEditedTag = useCallback(() => {
    if (newTag.trim() && isEditingTag && !tagError) {
      editTag({
        ...isEditingTag,
        tagValue: newTag,
      })
        .then((value) => {
          if (value) {
            onEditTagCancel();
          }
        })
        .catch(noop);
    }
  }, [editTag, isEditingTag, newTag, onEditTagCancel, tagError]);

  const onEditTagSave = useCallback(() => {
    validateTagName();
    !tagError && renameDialogRef?.current?.show();
  }, [tagError, validateTagName]);

  const onCancelEditingOrder = useCallback(() => {
    setIsEditingOrder(false);
    setTags([...initialTags.current]);
  }, []);

  const onSaveEditingOrder = useCallback(() => {
    handleReorder?.(updatedTags.current);
    updateTags?.(updatedTags.current, false);
    onSaveChanges?.(updatedTags.current);
    setIsEditingOrder(false);
  }, [handleReorder, onSaveChanges, updateTags]);

  const onReorderEditingOrder = useCallback(() => {
    if (!canEditTags) return;
    setIsEditingOrder(true);
  }, [canEditTags]);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditingTag && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingTag]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box className={'custom-tag-container-drawer'}>
        <Box>
          {canEditTags && (
          <Box
            className={
              isEditingTag
                ? 'custom-field-input-container custom-field-input-container-highlight'
                : 'custom-field-input-container'
            }
          >
            {isEditingTag && canEditTags && (
              <Typography className="typo-text headline1_size">
                {t('fieldManagement.customField.renameTag')}
              </Typography>
            )}
            <Box position={'relative'}>
              {duplicateError && (
                <Alert className="alert-tag" severity="error">
                  {t('common.tagDuplicate')}
                </Alert>
              )}
              {tagError && (
                <Alert className="alert-tag" severity="error">
                  {t('common.tagNameError')}
                </Alert>
              )}
              <Box className={'custom-field-input'}>
                <AurTextField
                  inputRef={inputRef}
                  value={newTag}
                  label={
                    isEditingTag
                      ? t('fieldManagement.customField.tagName')
                      : t('fieldManagement.customField.createNewTag')
                  }
                  ariaLabel={
                    isEditingTag
                      ? t('fieldManagement.customField.tagName')
                      : t('fieldManagement.customField.createNewTag')
                  }
                  helperText={t('common.fieldTagValidation')}
                  variant="filled"
                  fullWidth
                  minRows={1}
                  className={`aur-textfield custom-text-field text-input-field`}
                  inputProps={{
                    maxLength: 50,
                    'data-testid': 'tag-textfield-input',
                    'aria-label': t('fieldManagement.customField.addTagHint'),
                  }}
                  disabled={isEditingOrder}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {duplicateError ? (
                          <AurIcon
                            icon={IconNames.Error}
                            color={IconColors.Critical}
                            customClassName="heading-warning-icon"
                          />
                        ) : (
                          !isEditingTag && (
                            <div className="custom-field-update-buttons">
                              <AurButton
                                type={'continue'}
                                label={t('buttons.add')}
                                title={t('buttons.add')}
                                ariaLabel={t('buttons.addTagField')}
                                size="Medium"
                                buttonType="submit"
                                dataTestId="addTag-test"
                                classNames="header-button"
                                disabled={
                                  isEditingOrder ||
                                  isEmpty(newTag.trim()) ||
                                  duplicateError ||
                                  tagError
                                }
                                onClick={handleAddTag}
                              />
                            </div>
                          )
                        )}
                      </InputAdornment>
                    ),
                  }}
                  error={duplicateError || tagError}
                  onChange={handleChange}
                  onBlur={validateTagName}
                />

                {isEditingTag && (
                  <div className="custom-field-update-buttons">
                    <AurButton
                      type={'exit'}
                      label={t('buttons.cancel')}
                      title={t('buttons.cancel')}
                      ariaLabel={t('buttons.renameCancel')}
                      size="Medium"
                      onClick={onEditTagCancel}
                      dataTestId="rename-cancel-tag"
                      classNames="header-button"
                    />
                    <AurButton
                      type={'continue'}
                      label={t('buttons.save')}
                      title={t('buttons.save')}
                      ariaLabel={t('buttons.renameSave')}
                      size="Medium"
                      buttonType="submit"
                      dataTestId="rename-save-tag"
                      classNames="header-button"
                      disabled={
                        isEmpty(newTag.trim()) || duplicateError || tagError
                      }
                      onClick={onEditTagSave}
                    />
                  </div>
                )}
              </Box>
              {isEditingOrder ? (
                <Box className={'custom-field-edit-overlay'}></Box>
              ) : null}
            </Box>
          </Box>
                 )}
        </Box>
        
        <Box
          className={'custom-field-drawer-body'}
          sx={
            isEditingTag
              ? {
                  pointerEvents: 'none',
                  userSelect: 'none',
                }
              : undefined
          }
        >
          <Box
            className={
              'tag-header tag-header-top-radius tag-header-with-reorder-button'
            }
          >
            <Typography className="body2" tabIndex={0}>
              {t('fieldManagement.customField.name')}
            </Typography>
            {isEditingOrder ? (
              <Box className={'header-button-container'}>
                <AurButton
                  type={'exit'}
                  label={t('fieldManagement.customField.cancel')}
                  title={t('fieldManagement.customField.cancel')}
                  size="Medium"
                  dataTestId="reorder-tag-cancel"
                  classNames="header-button"
                  onClick={onCancelEditingOrder}
                />
                <AurButton
                  type={'submit'}
                  label={t('fieldManagement.customField.save')}
                  title={t('fieldManagement.customField.save')}
                  size="Medium"
                  dataTestId="reorder-tag-save"
                  classNames="header-button"
                  disabled={!isChangedInTags}
                  onClick={onSaveEditingOrder}
                />
              </Box>
            ) : (
              canEditTags && tags?.length > 0 && (
                <AurButton
                  disabled={tags?.length < 2}
                  type={'exit'}
                  label={t('fieldManagement.customField.reorder')}
                  title={t('fieldManagement.customField.reorder')}
                  size="Medium"
                  dataTestId="reorder-tag-button"
                  classNames="header-button"
                  onClick={onReorderEditingOrder}
                />
              )
            )}
          </Box>

          {tags.length > 0 ? (
            <>
              <Box className="drag-list">
                {tags.map((tag, index) => (
                <DraggableTag
                  isOrderingTag={isEditingOrder}
                  key={tag.tagValueId}
                  tag={tag}
                  index={index}
                  moveTag={isEditingOrder ? moveTag : noop}
                  handleDelete={canEditTags ? deleteTag : noop}
                  handleEdit={canEditTags ? onEditTagClicked : noop}
                  canEditTags={canEditTags}
                />
                ))}
              </Box>

              <Box className={'tag-header tag-header-bottom-radius'}>
                <Typography className={'body2'} tabIndex={0}>
                  {tags.length} {t('fieldManagement.customField.tags')}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography className={'typo-text body2_size no-tags-text'}>
              {t('fieldManagement.customField.noTags')}
            </Typography>
          )}
          {isEditingTag && <Box className={'custom-field-edit-overlay'}></Box>}
        </Box>
      </Box>
      {usedTagError && dismissUsedError && (
        <ErrorDialog
          title={t('fieldManagement.customField.usedErrorTag.title')}
          description={t(
            'fieldManagement.customField.usedErrorTag.description'
          )}
          button={t('common.okGotIt')}
          open={true}
          onOkClicked={dismissUsedError}
        />
      )}
      {showTagError && dismissTagError && (
        <ErrorDialog
          open={showTagError}
          title={t('fieldManagement.customField.duplicateErrorTag.title')}
          description={t(
            'fieldManagement.customField.duplicateErrorTag.description'
          )}
          button={t('common.okGotIt')}
          onOkClicked={dismissTagError}
        />
      )}
      <RenameConfirmDialog ref={renameDialogRef} onConfirm={updateEditedTag} />
    </DndProvider>
  );
};

export {DraggableTagList};
